import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, KeyRound, Loader2, ShieldCheck, UserRound } from 'lucide-react';
import { useState } from 'react';
import { DeviceSessions } from '../components/DeviceSessions';
import { PageNav } from '../components/PageNav';
import { ProfilePhoto } from '../components/ProfilePhoto';
import { PasswordFields, passwordPairReady } from '../components/PasswordFields';
import { api } from '../lib/api';
import { useSession } from '../lib/session';

/**
 * Your own account, and the only screen where you change your own password by
 * choice rather than because you were made to.
 *
 * This was the hole. A password could be set by an admin and forced once at
 * first sign-in, and after that there was no way to change it without asking
 * the admin to reset it — which hands the password to a second person again,
 * and is the opposite of what a change is for. Someone who thinks their
 * password has been seen needs to be able to replace it themselves, at the
 * moment they think it, without a phone call.
 *
 * No capability check: every signed-in role gets here, including technicians.
 * A screen about your own account cannot be a privilege.
 */
export function AccountPage() {
  const { user } = useSession();
  const [name, setName] = useState(user?.name ?? '');
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [savedName, setSavedName] = useState(false);
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const saveName = useMutation({
    mutationFn: () => api.updateMe(name.trim()),
    // A reload rather than patching local state: the name is rendered from the
    // session in the top bar and on every page, and one stale copy is exactly
    // the bug this avoids.
    onSuccess: () => {
      setSavedName(true);
      window.setTimeout(() => window.location.reload(), 700);
    },
    onError: (caught: Error) => {
      setDetailsError(caught.message);
      setSavedName(false);
    },
  });

  const change = useMutation({
    mutationFn: () => api.changePassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
      setDone(true);
    },
    onError: (caught: Error) => {
      setError(caught.message);
      setDone(false);
    },
  });

  const ready =
    current.length > 0 &&
    passwordPairReady(next, confirm, {
      email: user?.email,
      name: user?.name,
      currentPassword: current,
    });

  return (
    <div className="mx-auto max-w-2xl space-y-5">
      <PageNav crumbs={[{ label: 'Your account' }]} />

      <div>
        <h2 className="font-heading text-2xl">Your account</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          Your sign-in details, and where you change your own password.
        </p>
      </div>

      <form
        className="card space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          setDetailsError(null);
          saveName.mutate();
        }}
      >
        <div className="flex items-center gap-2">
          <UserRound className="h-5 w-5 text-primary-600" aria-hidden="true" />
          <h3 className="font-heading text-lg">Your details</h3>
        </div>

        {savedName && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            Saved.
          </p>
        )}

        {detailsError && (
          <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
            {detailsError}
          </p>
        )}

        <ProfilePhoto userId={user?.id} name={user?.name ?? ''} />

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Your name</span>
          <input
            className="input-field"
            value={name}
            onChange={(event) => setName(event.target.value)}
            minLength={2}
            maxLength={80}
            required
          />
          <span className="mt-1 block text-xs text-muted-foreground">
            This is what colleagues see on tickets and in the audit log.
          </span>
        </label>

        <button
          type="submit"
          className="btn-primary"
          disabled={
            saveName.isPending || name.trim().length < 2 || name.trim() === (user?.name ?? '')
          }
        >
          {saveName.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Save name
        </button>

        <div className="grid gap-3 border-t border-border pt-4 sm:grid-cols-2">
          {[
            ['Email', user?.email],
            ['Role', user?.role],
          ].map(([label, value]) => (
            <div key={label}>
              <dt className="text-xs label-mono tracking-[0.12em] text-muted-foreground">{label}</dt>
              <dd className="mt-0.5 break-words font-medium capitalize">{value ?? '—'}</dd>
            </div>
          ))}
        </div>

        {/*
          Stated rather than left as a missing field, because "why can't I edit
          my email" is otherwise a support question.
        */}
        <p className="flex items-start gap-2 rounded-xl bg-muted p-3 text-sm text-muted-foreground">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          Your email and role are changed by an admin, not here. Your email is what you sign in
          with — a typo would lock you out of your own account — and an account that can promote
          itself makes the permission rules decorative.
        </p>
      </form>

      <form
        className="card space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          setError(null);
          change.mutate();
        }}
      >
        <div className="flex items-center gap-2">
          <KeyRound className="h-5 w-5 text-primary-600" aria-hidden="true" />
          <h3 className="font-heading text-lg">Change your password</h3>
        </div>

        <p className="text-sm text-muted-foreground">
          You do not need anyone&apos;s help to do this, and you do not need a reason. If you think
          somebody has seen it, change it now.
        </p>

        {done && (
          <p
            role="status"
            className="flex items-center gap-2 rounded-xl bg-secondary-50 p-3 text-sm text-secondary-700"
          >
            <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            Password changed. Every other device you were signed in on has been signed out.
          </p>
        )}

        {error && (
          <p role="alert" className="rounded-xl bg-danger-50 p-3 text-sm text-danger-700">
            {error}
          </p>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium">Your current password</span>
          <input
            type="password"
            autoComplete="current-password"
            className="input w-full"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            required
          />
          {/*
            Required even though you are already signed in. Without it, anyone
            who walks past an unlocked screen takes the account permanently
            rather than just borrowing it for the afternoon.
          */}
          <span className="mt-1 block text-xs text-muted-foreground">
            Asked for so an unlocked screen cannot be used to take your account over.
          </span>
        </label>

        <PasswordFields
          value={next}
          confirm={confirm}
          onValueChange={setNext}
          onConfirmChange={setConfirm}
          email={user?.email}
          name={user?.name}
          currentPassword={current}
        />

        <button type="submit" className="btn-primary" disabled={!ready || change.isPending}>
          {change.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
          Change password
        </button>

        <p className="text-xs text-muted-foreground">
          Changing it signs out every other session you have open — a phone left at home, a browser
          on the office machine. This one stays signed in.
        </p>
      </form>

      <DeviceSessions />

      <div className="card">
        <h3 className="font-heading text-lg">If you are locked out</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          There is no email reset link. Ask an admin to set you a temporary password — they can do
          it in seconds, and you will be asked to choose your own the moment you sign in with it.
          For a team this size that is safer than a reset link anyone who reaches your inbox could
          use.
        </p>
      </div>
    </div>
  );
}
